import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {ChartBarIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {useNavigate} from "react-router-dom";
import {SponsorshipTier} from "types/SponsorshipTier";
import Dropdown from "components/Dropdown";
import DeleteSponsorshipTier from '../Delete';
import {useOperator} from "context/OperatorContext";

function SponsorshipTierList() {
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['sponsorship-tiers.all', {page}], () => ngage.sponsorshipTiers.all(page), {keepPreviousData: true})

    const [deletingSponsorshipTier, setDeletingSponsorshipTier] = useState<SponsorshipTier | null>(null);

    const {termFor} = useOperator()
    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('sponsorship-tiers.all', page, ngage.sponsorshipTiers.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/sponsorship-tiers/new')}>New</Button>

    // Prepare breadcrumbs
    const breadcrumbs = {
        'Commercial': '/',
        [`${termFor('Sponsorship')} Tiers`]: '/sponsorship-tiers'
    }

    return (
        <Page title={`${termFor('Sponsorship')} Tiers`} actions={actions} breadcrumbs={breadcrumbs}>
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader>Duration</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Price</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Sponsorships')}</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={6}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <ChartBarIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No {termFor('sponsorship')} tiers</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((sponsorshipTier, index) => (
                        <TableRow key={sponsorshipTier.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/sponsorship-tiers/${sponsorshipTier.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/sponsorship-tiers/${sponsorshipTier.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingSponsorshipTier(sponsorshipTier), disabled: sponsorshipTier.sponsorship_count > 0}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <ChartBarIcon className="h-5 w-5 mr-2 text-gray-500" /> {sponsorshipTier.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(sponsorshipTier.description, 50)}</TableData>
                            <TableData>{sponsorshipTier.duration.toHuman()}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{sponsorshipTier.price? sponsorshipTier.price_formatted : 'Free'}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{sponsorshipTier.sponsorship_count}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/sponsorship-tiers/${sponsorshipTier.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/sponsorship-tiers/${sponsorshipTier.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingSponsorshipTier(sponsorshipTier), disabled: sponsorshipTier.sponsorship_count > 0}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteSponsorshipTier
                                sponsorshipTier={deletingSponsorshipTier}
                                show={sponsorshipTier === deletingSponsorshipTier}
                                onCancel={() => setDeletingSponsorshipTier(null)}
                                onDelete={() => navigate('/sponsorship-tiers')}
                                onDeleteFail={() => setDeletingSponsorshipTier(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default SponsorshipTierList;